import React from 'react';
import { createContext, useContext, useState, useEffect } from 'react';
import { auth } from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Only check auth if we have a token
    const token = localStorage.getItem('token');
    if (token) {
      checkAuth();
    } else {
      setLoading(false);
    }
  }, []);

  const checkAuth = async () => {
    try {
      const { data } = await auth.me();
      console.log('Auth check response:', data); // Debug log
      // Handle the nested user object in the response
      const userData = data.data?.user || data.user || data;
      console.log('Processed user data:', userData); // Debug log
      setUser(userData);
    } catch (error) {
      console.error('Auth check error:', error);
      setUser(null);
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  };

  const login = async (credentials) => {
    const response = await auth.login(credentials);
    console.log('Login response:', response.data); // Debug log
    // Handle the nested user object in the response
    const userData = response.data.data?.user || response.data.user;
    const token = response.data.data?.token || response.data.token;
    
    if (userData && token) {
      console.log('Setting user data:', userData); // Debug log
      setUser(userData);
      localStorage.setItem('token', token);
    } else {
      throw new Error('Invalid response format');
    }
    
    return response.data;
  };

  const register = async (userData) => {
    const response = await auth.register(userData);
    console.log('Register response:', response.data); // Debug log
    // Handle the nested user object in the response
    const registeredUser = response.data.data?.user || response.data.user;
    const token = response.data.data?.token || response.data.token;
    
    if (registeredUser && token) {
      console.log('Setting registered user:', registeredUser); // Debug log
      setUser(registeredUser);
      localStorage.setItem('token', token);
    } else {
      throw new Error('Invalid response format');
    }
    
    return response.data;
  };

  const logout = async () => {
    try {
      await auth.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      localStorage.removeItem('token');
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 